/**
 * Upload Context
 * Global upload state management — Google Drive-style background uploads
 * Allows users to navigate freely while uploads continue in the background
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { uploadLecture, uploadSlide } from '../api/courses';

export interface UploadItem {
  id: string;
  fileName: string;
  fileType: 'lecture' | 'slide';
  progress: number;
  status: 'uploading' | 'done' | 'error';
  courseId: string;
  error?: string;
}

interface UploadContextValue {
  uploads: UploadItem[];
  startLectureUpload: (
    courseId: string,
    title: string,
    description: string,
    file: File,
    onComplete?: () => void,
  ) => void;
  startSlideUpload: (
    courseId: string,
    title: string,
    file: File,
    onComplete?: () => void,
  ) => void;
  dismissUpload: (id: string) => void;
  dismissCompleted: () => void;
  hasActiveUploads: boolean;
}

const UploadContext = createContext<UploadContextValue | undefined>(undefined);

export const UploadProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const autoDismissTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const hasActiveUploads = uploads.some(u => u.status === 'uploading');

  // Warn before closing tab during active uploads
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasActiveUploads) {
        e.preventDefault();
        e.returnValue = 'You have uploads in progress. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasActiveUploads]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      autoDismissTimers.current.forEach(timer => clearTimeout(timer));
    };
  }, []);

  const dismissUpload = useCallback((id: string) => {
    const timer = autoDismissTimers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      autoDismissTimers.current.delete(id);
    }
    setUploads(prev => prev.filter(u => u.id !== id));
  }, []);

  const dismissCompleted = useCallback(() => {
    setUploads(prev => prev.filter(u => u.status === 'uploading'));
    autoDismissTimers.current.forEach((timer, id) => {
      clearTimeout(timer);
      autoDismissTimers.current.delete(id);
    });
  }, []);

  const scheduleAutoDismiss = useCallback((id: string) => {
    const timer = setTimeout(() => {
      autoDismissTimers.current.delete(id);
      setUploads(prev => prev.filter(u => u.id !== id));
    }, 8000);
    autoDismissTimers.current.set(id, timer);
  }, []);

  const startLectureUpload = useCallback((
    courseId: string,
    title: string,
    description: string,
    file: File,
    onComplete?: () => void,
  ) => {
    const id = `upload_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const item: UploadItem = {
      id,
      fileName: title || file.name,
      fileType: 'lecture',
      progress: 0,
      status: 'uploading',
      courseId,
    };

    setUploads(prev => [...prev, item]);

    uploadLecture(courseId, title, description, file, (progressEvent) => {
      const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
      setUploads(prev => prev.map(u => u.id === id ? { ...u, progress: percent } : u));
    })
      .then(() => {
        setUploads(prev => prev.map(u => u.id === id ? { ...u, status: 'done', progress: 100 } : u));
        scheduleAutoDismiss(id);
        onComplete?.();
      })
      .catch((err: any) => {
        setUploads(prev => prev.map(u => u.id === id ? {
          ...u,
          status: 'error',
          error: err.response?.data?.detail || 'Upload failed',
        } : u));
      });
  }, [scheduleAutoDismiss]);

  const startSlideUpload = useCallback((
    courseId: string,
    title: string,
    file: File,
    onComplete?: () => void,
  ) => {
    const id = `upload_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const item: UploadItem = {
      id,
      fileName: title || file.name,
      fileType: 'slide',
      progress: 0,
      status: 'uploading',
      courseId,
    };

    setUploads(prev => [...prev, item]);

    uploadSlide(courseId, title, file, (progressEvent) => {
      const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
      setUploads(prev => prev.map(u => u.id === id ? { ...u, progress: percent } : u));
    })
      .then(() => {
        setUploads(prev => prev.map(u => u.id === id ? { ...u, status: 'done', progress: 100 } : u));
        scheduleAutoDismiss(id);
        onComplete?.();
      })
      .catch((err: any) => {
        setUploads(prev => prev.map(u => u.id === id ? {
          ...u,
          status: 'error',
          error: err.response?.data?.detail || 'Upload failed',
        } : u));
      });
  }, [scheduleAutoDismiss]);

  return (
    <UploadContext.Provider value={{
      uploads,
      startLectureUpload,
      startSlideUpload,
      dismissUpload,
      dismissCompleted,
      hasActiveUploads,
    }}>
      {children}
    </UploadContext.Provider>
  );
};

export const useUpload = (): UploadContextValue => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error('useUpload must be used within an UploadProvider');
  }
  return context;
};
